import { faCheck, faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RootState } from "src/Store/store";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { CURRENCY, stripeCardNumberInputStyles } from "src/lib/constans";
import {
  ICustomJwtPayload,
  isProfileCompleted,
} from "src/helpers/helperFunctions";
import EasyPaisa from "src/assets/images/payment-method/easy-paisa.png";
import Jazzcash from "src/assets/images/payment-method/jazz-cash.png";
import Debits from "src/assets/images/payment-method/credit-card.png";
import { toast } from "react-toastify";

interface IProps {
  containerClassName?: string;
  customFormFooter?: (data: { isLoader: boolean }) => React.ReactNode;
  onCardPayment?: (data: any) => void;
  profileValidation?: boolean;
  addressValidation?: boolean;
  loginValidation?: boolean;
  cardPaymentConfig: {
    onSuccess?: (data: { paymentIndentId: string }) => void;
    onError?: (error: { type: string; message: string }) => void;
    onCardPayment?: (data: any) => void;
    validation?: {
      beforeSubmit?: (
        event: React.FormEvent<HTMLFormElement>,
        stripeOptions: {
          cardNumberElement: typeof CardNumberElement;
          cardExpiryElement: typeof CardExpiryElement;
          cardCvcElement: typeof CardCvcElement;
        }
      ) => Promise<boolean>;
    };
  };
  onEasyPaisaPayment?: (data: any) => void;
  onJazzcashPayment?: (data: any) => void;
}

const stripePromise = loadStripe(process.env.REACT_APP_VITE_STRIPE_PK || "");

const CardPaymentForm = ({
  customFormFooter,
  cardPaymentConfig,
  profileValidation,
  addressValidation,
  loginValidation,
}: {
  customFormFooter?: (data: { isLoader: boolean }) => React.ReactNode;
  cardPaymentConfig: {
    onSuccess?: (data: { paymentIndentId: string }) => void;
    onError?: (error: { type: string; message: string }) => void;
    onCardPayment?: (data: any) => void;
    validation?: {
      beforeSubmit?: (
        event: React.FormEvent<HTMLFormElement>,
        stripeOptions: {
          cardNumberElement: typeof CardNumberElement;
          cardExpiryElement: typeof CardExpiryElement;
          cardCvcElement: typeof CardCvcElement;
        }
      ) => Promise<boolean>;
    };
  };
  profileValidation?: boolean;
  addressValidation?: boolean;
  loginValidation?: boolean;
}) => {
  const { isLogged, userDetails } = useSelector(
    (store: RootState) => store.appReducer
  );
  const { address } = useSelector((store: RootState) => store.cartSlice);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  // Stripe Variables Handle
  const stripe = useStripe();
  const elements = useElements();

  const handleCardPaymentSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (loginValidation && !isLogged) {
      return cardPaymentConfig.onError?.({
        type: "login",
        message: "Please login to continue",
      });
    }

    if (
      profileValidation &&
      !isProfileCompleted(userDetails as ICustomJwtPayload)
    ) {
      return cardPaymentConfig.onError?.({
        type: "profile",
        message: "Please fill your profile to continue",
      });
    }

    if (addressValidation && !address) {
      return cardPaymentConfig.onError?.({
        type: "address",
        message: "Please fill your address to continue",
      });
    }

    if (!stripe || !elements) {
      toast("Stripe is not initialized. Please try again.", { type: "error" });
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);
    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      toast("Card details are incomplete. Please fill all fields.", {
        type: "error",
      });
      return;
    }

    setIsLoader(true);

    if (cardPaymentConfig.validation?.beforeSubmit) {
      const isValid = await cardPaymentConfig.validation.beforeSubmit(event, {
        cardNumberElement: cardNumberElement as any,
        cardExpiryElement: cardExpiryElement as any,
        cardCvcElement: cardCvcElement as any,
      });
      if (!isValid) {
        setIsLoader(false);
        return;
      }
    }

    if (cardPaymentConfig.onCardPayment) {
      cardPaymentConfig.onCardPayment?.(event);
      setIsLoader(false);
      return;
    }


    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
      });

      if (error) {
        toast(error.message || "Payment validation failed.", { type: "error" });
        console.error("Stripe error:", error);
      } else if (paymentMethod) {
        console.log("Payment Method Created:", paymentMethod);
        toast("Payment method created successfully!", { type: "success" });
        cardPaymentConfig.onSuccess?.({
          paymentIndentId: paymentMethod.id,
        });
      }
    } catch (err) {
      toast("An unexpected error occurred. Please try again.", {
        type: "error",
      });
      console.error("Unexpected error:", err);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <form
      className="bg-gray-50 side-bar-padding 2xl:rounded-lg rounded-md"
      onSubmit={handleCardPaymentSubmit}
      id="payment-method-form"
    >
      <h3 className="font-Poppins-Medium font-size-18px text-[#202020] mb-3">
        Card Payment
      </h3>
      <div className="grid grid-cols-1 2xl:gap-2 gap-1">
        <div>
          <div className="visa-card-number relative">
            <div className="card-icon-checkout">
              <FontAwesomeIcon
                icon={faCreditCard}
                className="2xl:text-[18px] text-[15px]"
              />
            </div>
            <CardNumberElement
              className="stripe-des font-size-16px-important font-Poppins-Regular paymnet-form-input 2xl:pl-[calc(32px+0.6rem)] pl-[calc(24px+0.4rem)]"
              id="cardNumber"
              options={{
                ...stripeCardNumberInputStyles,
              }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:gap-3 gap-1.5">
          <div>
            <CardExpiryElement
              className="stripe-des font-size-16px-important font-Poppins-Regular paymnet-form-input"
              id="CardExpiryElement"
              options={{
                ...stripeCardNumberInputStyles,
              }}
            />
          </div>
          <div>
            <CardCvcElement
              className="stripe-des font-size-16px-important font-Poppins-Regular paymnet-form-input"
              id="CardCvcElement"
              options={{
                ...stripeCardNumberInputStyles,
              }}
            />
          </div>
        </div>
      </div>
      {customFormFooter?.({ isLoader } as any)}
    </form>
  );
};

const PaymentMethodComponents: React.FC<IProps> = ({
  containerClassName,
  customFormFooter,
  cardPaymentConfig,
  profileValidation = false,
  addressValidation = true,
  loginValidation = true,
  onEasyPaisaPayment,
  onJazzcashPayment,
}) => {
  // Handle Payment Method State
  let [paymentMethod, setPaymentMethod] = useState<any>("debits");
  const [isLoader, setIsLoader] = useState<boolean>(false);
  // Global States
  const { address } = useSelector((store: RootState) => store.cartSlice);
  const { isLogged, userDetails } = useSelector(
    (store: RootState) => store.appReducer
  );

  // Handle Submit

  let paymentMethodList = [
    {
      id: "debits",
      name: "Card",
      image: Debits,
      imageClass: "scale-[0.7]",
    },
    // {
    //   id: "easy-paisa",
    //   name: "EasyPaisa",
    //   image: EasyPaisa,
    //   imageClass: "",
    // },
    // {
    //   id: "jazzcash",
    //   name: "Jazzcash",
    //   image: Jazzcash,
    //   imageClass: "",
    // },
  ];

  return (
    <div className={`w-full box-design side-bar-padding ${containerClassName}`}>
      <p className="font-Poppins-Medium font-size-18px text-[#202020] pb-2">
        Payment Method
      </p>
      <div className="w-full flex flex-col gap-2 mb-4">
        {paymentMethodList.map((item) => (
          <div
            key={item.id}
            className={`flex items-center 2xl:gap-2 gap-1 2xl:px-3 px-2 2xl:py-1 py-0.5 2xl:rounded-lg rounded-md border cursor-pointer transition-all justify-between ${
              paymentMethod === item.id
                ? "border-[#22C55E] bg-[#F0FDF4]"
                : "border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => setPaymentMethod(item.id)}
          >
            <div className="flex items-center gap-2">
              <div className="2xl:w-9 2xl:h-8 w-8 h-7 flex items-center justify-center">
                <img
                  src={item.image}
                  alt={item.name}
                  className={`w-full object-contain ${item.imageClass}`}
                />
              </div>
              <p className="font-Poppins-Medium font-size-16px text-[#202020]">
                {item.name}
              </p>
            </div>
            {paymentMethod === item.id && (
              <div className="lg:w-[1vw] lg:h-[1vw] w-[15px] h-[15px] flex items-center justify-center rounded-full bg-[#22C55E]">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-white lg:text-[0.6vw] text-[9px]"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {paymentMethod === "debits" ? (
        <Elements stripe={stripePromise}>
          <CardPaymentForm
            cardPaymentConfig={cardPaymentConfig}
            profileValidation={profileValidation}
            addressValidation={addressValidation}
            loginValidation={loginValidation}
            customFormFooter={customFormFooter as any}
          />
        </Elements>
      ) : paymentMethod === "easy-paisa" ? (
        <div className="bg-gray-50 side-bar-padding 2xl:rounded-lg rounded-md">
          <h3 className="font-Poppins-Medium font-size-18px text-[#202020] mb-3">
            EasyPaisa Payment
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block font-Poppins-Medium font-size-14px text-[#666] mb-1">
                Mobile Number
              </label>
              <input
                type="tel"
                placeholder="03XXXXXXXXX"
                className="w-full p-3 border border-gray-300 rounded-lg font-Poppins-Regular font-size-16px focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block font-Poppins-Medium font-size-14px text-[#666] mb-1">
                CNIC Number
              </label>
              <input
                type="text"
                placeholder="35202-1234567-1"
                className="w-full p-3 border border-gray-300 rounded-lg font-Poppins-Regular font-size-16px focus:outline-none focus:border-blue-500"
              />
            </div>
            {customFormFooter?.({ isLoader: false } as any)}
          </div>
        </div>
      ) : paymentMethod === "jazzcash" ? (
        <div className="bg-gray-50 side-bar-padding 2xl:rounded-lg rounded-md">
          <h3 className="font-Poppins-Medium font-size-18px text-[#202020] mb-3">
            JazzCash Payment
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block font-Poppins-Medium font-size-14px text-[#666] mb-1">
                Mobile Number
              </label>
              <input
                type="tel"
                placeholder="03XXXXXXXXX"
                className="w-full p-3 border border-gray-300 rounded-lg font-Poppins-Regular font-size-16px focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block font-Poppins-Medium font-size-14px text-[#666] mb-1">
                CNIC Number
              </label>
              <input
                type="text"
                placeholder="35202-1234567-1"
                className="w-full p-3 border border-gray-300 rounded-lg font-Poppins-Regular font-size-16px focus:outline-none focus:border-blue-500"
              />
            </div>
            {customFormFooter?.({ isLoader: false } as any)}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentMethodComponents;
